import { useEffect, useState, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  EPOverview,
  EPData,
  EPChat,
  EPTools,
  EPTranscript,
} from '@rsos/assets/icons';
import { BE_END_CALL } from '@rsos/constants/callStatuses';
import { useFlag } from '@rsos/flags';
import {
  setSelectedTab,
  resetState,
} from '@rsos/reduxDataStore/src/features/eventPane/eventPaneSlice';
import {
  TOOLS_TAB,
  CHAT_TAB,
  OVERVIEW_TAB,
  DATA_TAB,
  TRANSCRIPT_TAB,
} from '@rsos/reduxDataStore/src/features/eventPane/eventPaneTabs';
import { useGetEventSharesByPhoneQuery } from '@rsos/reduxDataStore/src/features/eventShare/eventShareApi';
import { ROADSIDE_ASSISTANCE_FEATURE } from '@rsos/reduxDataStore/src/features/outboundSms/outboundSmsSlice';
import { dismissRcsToast } from '@rsos/reduxDataStore/src/features/rcs/rcsSlice';
import { useCurrentOrgName } from '@rsos/sinatra/src/selectors';
import getIsPrimaryChatTab from '@rsos/utils/getIsPrimaryChatTab';
import {
  trackTranscriptionTabOpen,
  trackTranscriptionTabDuration,
} from '../../../gainsightTrackings/callTranscriptionTrackings';
import {
  trackEventPaneOpen,
  trackEventPaneClose,
  trackEventPaneTabOpen,
} from '../../../gainsightTrackings/eventPaneTrackings';
import useFetchEmergencySessionForTel from '../../../hooks/useFetchEmergencySessionForTel';
import useHasAccessToCommunicatorTranscription from '../../../hooks/useHasAccessToCommunicatorTranscription';
import What3WordsSearchResult from '../../What3Words/What3WordsSearchResult';
import { useChatComponents } from '../useChatComponents';
import { StyledEventPane } from './EventPane.styles';
import EventPaneHeader from './EventPaneHeader';
import { TabSwitcher, Tab, TabLabel } from './EventPaneTabs';
import ChatsTab from './tabs/ChatsTab';
import DataCards from './tabs/DataCards';
import OverviewTab from './tabs/OverviewTab';
import ToolsTab from './tabs/ToolsTab';
import TranscriptionTab from './tabs/TranscriptTab/TranscriptionTab';
import { useFetchTranscriptionsQuery } from './tabs/TranscriptTab/transcriptionSlice';
import useCallDetails from './useCallDetails';

/**
 * EventPane component displays event details and contextual options.
 *
 * @param {Object} props - The props object.
 * @param {string} props.callerID - The phone number for the selected call
 * @param {boolean} [props.isInternational=false] - Flag to indicate if the call is international.
 * @param {Function} [props.onActionableButton=() => {}] - Callback function triggered for circus ERA
 * @param {Function} [props.onClose=() => {}] - Callback function triggered when the close button is clicked.
 *
 * @returns {JSX.Element} The EventPane component.
 */
const EventPane = ({
  callerID,
  hasW3w = false,
  isInternational = false,
  onActionableButton = () => {},
  onClose = () => {},
}) => {
  const dispatch = useDispatch();
  const uniteSharingFlag = useFlag('unite_sharing');
  // Fire off a transcription when the callerID changes
  useFetchTranscriptionsQuery(callerID, { refetchOnMountOrArgChange: true });

  useFetchEmergencySessionForTel(callerID);
  const orgName = useCurrentOrgName();

  // Fetch event shares for the current call
  useGetEventSharesByPhoneQuery(
    {
      org: orgName || '',
      phoneNumber: callerID ? `tel://${callerID}` : '',
    },
    {
      skip: !orgName || !callerID || !uniteSharingFlag,
    },
  );

  const [accordionHeight, setAccordionHeight] = useState(0);

  const callDetails = useCallDetails(callerID);
  const hasAccessToCommunicatorTranscript =
    useHasAccessToCommunicatorTranscription();
  const selectedFeature = useSelector(
    state => state.outboundSms.selectedFeature,
  );
  const selectedTab = useSelector(state => state.eventPane.selectedTab);
  const isOnlyChatTab = getIsPrimaryChatTab(callDetails.lastLocation);

  const isAllCallsSelected = useSelector(
    state => !state.emergencyCalls.selectedCallerID,
  );

  const hideDataTab = callDetails.isAdmin || isOnlyChatTab;

  const hideTranscriptTab = !hasAccessToCommunicatorTranscript || isOnlyChatTab;

  const isFirstRender = useRef(true);
  const transcriptionTabStartTime = useRef(null);
  const transcriptionTabCallState = useRef(null);

  const isCollapsed = useSelector(state => state.eventPane.isCollapsed);

  // Track EventPane open when component mounts
  useEffect(() => {
    trackEventPaneOpen();
  }, []);

  // Track EventPane close when component unmounts (covers all close scenarios)
  useEffect(() => {
    return () => {
      // Track linger time if EventPane closes while on Transcription tab
      if (
        transcriptionTabStartTime.current !== null &&
        transcriptionTabCallState.current !== null
      ) {
        const durationSeconds =
          (Date.now() - transcriptionTabStartTime.current) / 1000;

        trackTranscriptionTabDuration({
          callState: transcriptionTabCallState.current,
          durationSeconds,
        });
      }
      trackEventPaneClose();
    };
  }, []);

  // Track tab changes (skip initial render)
  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }

    trackEventPaneTabOpen({ tabId: selectedTab });

    // Track linger time when leaving the Transcription tab
    if (
      transcriptionTabStartTime.current !== null &&
      transcriptionTabCallState.current !== null &&
      selectedTab !== TRANSCRIPT_TAB
    ) {
      const durationSeconds =
        (Date.now() - transcriptionTabStartTime.current) / 1000;

      trackTranscriptionTabDuration({
        callState: transcriptionTabCallState.current,
        durationSeconds,
      });
      transcriptionTabStartTime.current = null;
      transcriptionTabCallState.current = null;
    }

    // Track when entering the Transcription tab
    if (selectedTab === TRANSCRIPT_TAB) {
      const callState =
        callDetails.callStatus === BE_END_CALL ? 'ended' : 'active';

      trackTranscriptionTabOpen({ callState });
      transcriptionTabStartTime.current = Date.now();
      transcriptionTabCallState.current = callState;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedTab]);

  useEffect(() => {
    dispatch(dismissRcsToast({ callerID }));
    if (selectedFeature === ROADSIDE_ASSISTANCE_FEATURE) {
      dispatch(setSelectedTab(CHAT_TAB));
    }
  }, [callerID, dispatch, selectedFeature]);

  useEffect(() => {
    return () => {
      dispatch(resetState());
    };
  }, [dispatch]);

  const { isChatDisabled } = useChatComponents();

  return (
    <StyledEventPane isCollapsed={isCollapsed}>
      {hasW3w && (
        <What3WordsSearchResult
          isAllCallsSelected={isAllCallsSelected}
          hasW3w={hasW3w}
          setAccordionHeight={setAccordionHeight}
          accordionHeight={accordionHeight}
        />
      )}
      <EventPaneHeader
        callerID={callDetails.callerID}
        isInternational={isInternational}
        source={callDetails.source}
        timestamp={callDetails.lastTimestamp}
        onClose={onClose}
      />
      {!isCollapsed && (
        <TabSwitcher>
          <Tab
            id={OVERVIEW_TAB}
            data-name="overviewTab"
            Label={<TabLabel Icon={EPOverview} text="Overview" />}
          >
            <OverviewTab
              onDataCardClick={() => {
                dispatch(setSelectedTab('data'));
              }}
              callerID={callerID}
            />
          </Tab>
          <Tab
            id={DATA_TAB}
            data-name="dataTab"
            Label={<TabLabel Icon={EPData} text="Data" />}
            hidden={hideDataTab}
          >
            <DataCards onActionableButton={onActionableButton} />
          </Tab>
          <Tab
            id={TRANSCRIPT_TAB}
            data-name="transcriptTab"
            hidden={hideTranscriptTab}
            Label={<TabLabel Icon={EPTranscript} text="Transcript" />}
          >
            <TranscriptionTab callerId={callDetails.callerID} />
          </Tab>
          <Tab
            id={CHAT_TAB}
            data-name="chatTab"
            Label={<TabLabel Icon={EPChat} text="Chat" />}
            disabled={isChatDisabled}
          >
            <ChatsTab />
          </Tab>
          {
            <Tab
              id={TOOLS_TAB}
              Label={<TabLabel Icon={EPTools} text="Tools" />}
            >
              <ToolsTab />
            </Tab>
          }
        </TabSwitcher>
      )}
    </StyledEventPane>
  );
};

export default EventPane;
